import {
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    Image,
    View
} from "react-native";
import type CompanyDetailModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Sticker,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    CompanyRequestDetailPayloadType
} from "../../../../services/restAPI/actions/getCompanyUpdateRequest/type";
import {
    CheckIcon,
    XIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";

const CompanyDetailModal = ({
    historyItem = null,
    isHistory = false,
    isVisible,
    onApprove,
    requestID,
    onReject,
    onClose
}: CompanyDetailModalProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        detail,
        setDetail
    ] = useState<CompanyRequestDetailPayloadType | null>(null);

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    useEffect(() => {
        if (!isVisible) {
            setDetail(null);
            return;
        }

        if (historyItem) {
            setDetail({
                requestedChanges: {
                    foundationYear: historyItem.foundationYear,
                    phoneNumber: historyItem.phoneNumber,
                    sectorIDs: historyItem.sectorIDs,
                    taxNumber: historyItem.taxNumber,
                    taxOffice: historyItem.taxOffice,
                    logoPath: historyItem.logoPath,
                    aboutUs: historyItem.aboutUs,
                    address: historyItem.address,
                    website: historyItem.website,
                    title: historyItem.title,
                    mail: historyItem.mail
                },
                positionName: historyItem.positionName,
                requestType: historyItem.requestType,
                createdAt: historyItem.createdAt,
                requestID: historyItem.historyID,
                userID: historyItem.userID
            });
            setLoading(false);
            return;
        }

        if (!requestID) {
            setDetail(null);
            return;
        }

        const fetchDetail = async () => {
            try {
                setLoading(true);
                const response = await RESTServiceContext.action("getCompanyUpdateRequest", {
                    requestID
                });

                if (response.payload) {
                    setDetail(response.payload);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [
        historyItem,
        requestID,
        isVisible
    ]);

    const renderCurrentDiffValue = (isPhoto?: boolean, currentValue?: string) => {
        if (isPhoto) {
            if (!currentValue) {
                return <Text
                    customColor={colors.content.text.low}
                    variant="bodySmallSize"
                >
                    -
                </Text>;
            }

            return <Image
                style={[
                    stylesheet.logoPreview,
                    {
                        borderRadius: radiuses.md
                    }
                ]}
                source={{
                    uri: currentValue
                }}
            />;
        }

        return <Text
            customColor={colors.content.text.mid}
            variant="bodyMediumSize"
        >
            {currentValue || "-"}
        </Text>;
    };

    const renderRequestedDiffValue = (isPhoto?: boolean, requestedValue?: string, isChanged?: boolean) => {
        if (isPhoto) {
            if (!requestedValue) {
                return <Text
                    customColor={colors.content.text.low}
                    variant="bodySmallSize"
                >
                    -
                </Text>;
            }

            return <View
                style={[
                    stylesheet.badgeContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Image
                    style={[
                        stylesheet.logoPreview,
                        {
                            borderColor: isChanged ? colors.system.default.success : colors.content.border.subtle,
                            borderRadius: radiuses.md,
                            borderWidth: borders.line
                        }
                    ]}
                    source={{
                        uri: requestedValue
                    }}
                />
                {
                    isChanged ?
                        <View
                            style={stylesheet.badgeContainer}
                        >
                            <Sticker
                                title={localize("requests-diff-changed")}
                                spreadBehaviour="free"
                                type="success"
                                size="small"
                            />
                        </View>
                    :
                        null
                }
            </View>;
        }

        return <View
            style={[
                stylesheet.requestedValueWrapper,
                {
                    gap: spaces.spacingXs
                }
            ]}
        >
            <Text
                customColor={colors.content.text.high}
                variant="bodyMediumSize"
            >
                {requestedValue || "-"}
            </Text>
            {
                isChanged ?
                    <View
                        style={stylesheet.badgeContainer}
                    >
                        <Sticker
                            title={localize("requests-diff-changed")}
                            spreadBehaviour="free"
                            type="success"
                            size="small"
                        />
                    </View>
                :
                    null
            }
        </View>;
    };

    const renderDiffRow = (
        label: string,
        currentValue?: string,
        requestedValue?: string,
        isPhoto: boolean = false
    ) => {
        const isChanged = requestedValue !== undefined && requestedValue !== currentValue;

        return <View
            style={[
                stylesheet.diffRow,
                {
                    borderColor: colors.content.border.subtle,
                    borderBottomWidth: borders.seperator,
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
            key={label}
        >
            <View
                style={stylesheet.fieldLabelContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="labelMediumSize"
                >
                    {label}
                </Text>
            </View>
            <View
                style={stylesheet.currentValueContainer}
            >
                {renderCurrentDiffValue(isPhoto, currentValue)}
            </View>
            <View
                style={stylesheet.requestedValueContainer}
            >
                {renderRequestedDiffValue(isPhoto, requestedValue, isChanged)}
            </View>
        </View>;
    };

    const renderContent = () => {
        if (loading) {
            return <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        if (!detail) {
            return <View
                style={[
                    stylesheet.errorContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-detail-load-error")}
                </Text>
            </View>;
        }

        const current = detail.currentCompany || {};
        const requested = detail.requestedChanges || {};
        const isNew = detail.requestType === "new";

        const currentTax = current.taxNumber ? `${current.taxNumber} (${current.taxOffice || ""})` : "";
        const requestedTax = requested.taxNumber ? `${requested.taxNumber} (${requested.taxOffice || ""})` : "";

        return <ScrollView
            style={stylesheet.contentContainer}
            showsVerticalScrollIndicator={true}
        >
            <View
                style={[
                    stylesheet.badgeContainer,
                    {
                        marginBottom: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Sticker
                    title={isNew ? localize("requests-type-new-company") : localize("requests-type-edit-company")}
                    type={isNew ? "warning" : "success"}
                    spreadBehaviour="free"
                    size="small"
                />
                {
                    detail.userName ?
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodySmallSize"
                        >
                            {localize("requests-submitted-by")}: {detail.userName} {detail.positionName ? `(${detail.positionName})` : ""}
                        </Text>
                    :
                        null
                }
            </View>
            <View
                style={stylesheet.diffTableContainer}
            >
                <View
                    style={[
                        stylesheet.tableHeaderRow,
                        {
                            backgroundColor: colors.content.container.subtle,
                            paddingHorizontal: spaces.spacingMd,
                            paddingVertical: spaces.spacingSm,
                            borderRadius: radiuses.sm,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.fieldLabelContainer}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelSmallSize"
                        >
                            {localize("requests-diff-field")}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.currentValueContainer}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelSmallSize"
                        >
                            {isNew ? "-" : localize("requests-diff-current")}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.requestedValueContainer}
                    >
                        <Text
                            customColor={colors.content.text.low}
                            variant="labelSmallSize"
                        >
                            {localize("requests-diff-requested")}
                        </Text>
                    </View>
                </View>
                {renderDiffRow(localize("requests-field-title"), current.title, requested.title)}
                {renderDiffRow(localize("requests-field-about"), current.aboutUs, requested.aboutUs)}
                {renderDiffRow(localize("requests-field-address"), current.address, requested.address)}
                {renderDiffRow(localize("requests-field-phone"), current.phoneNumber, requested.phoneNumber)}
                {renderDiffRow(localize("requests-field-mail"), current.mail, requested.mail)}
                {renderDiffRow(localize("requests-field-website"), current.website, requested.website)}
                {renderDiffRow(localize("requests-field-tax"), currentTax, requestedTax)}
                {renderDiffRow(localize("requests-field-foundation-year"), current.foundationYear ? String(current.foundationYear) : undefined, requested.foundationYear ? String(requested.foundationYear) : undefined)}
                {renderDiffRow(localize("requests-field-logo"), current.logoPath, requested.logoPath, true)}
            </View>
        </ScrollView>;
    };

    return <Modal
        id="company-detail-modal"
        portalName="modal-system"
        isActive={isVisible}
        onClosed={onClose}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderRadius: radiuses.lg,
                    padding: spaces.spacingXl,
                    gap: spaces.spacingLg
                }
            ]}
        >
            <View
                style={stylesheet.headerContainer}
            >
                <Text
                    customColor={colors.content.text.high}
                    variant="titleMediumSize"
                >
                    {localize("requests-detail-company-title")}
                </Text>
                <TouchableOpacity
                    style={stylesheet.closeButton}
                    onPress={onClose}
                >
                    <XIcon
                        color={colors.content.icon.mid}
                        size={24}
                    />
                </TouchableOpacity>
            </View>
            {renderContent()}
            <View
                style={[
                    stylesheet.footerContainer,
                    {
                        justifyContent: !isHistory && (onReject || onApprove) ? "space-between" : "flex-end"
                    }
                ]}
            >
                <Button
                    title={localize("notifications-detail-close")}
                    customTextColor={colors.project.brandWhite}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    onPress={onClose}
                    variant="filled"
                />
                {
                    !isHistory && (onReject || onApprove) ?
                        <View
                            style={[
                                stylesheet.footerActionsContainer,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {
                                onReject ?
                                    <Button
                                        onPress={() => requestID && onReject(requestID)}
                                        title={localize("requests-action-reject")}
                                        icon={({
                                            color,
                                            size
                                        }) => <XIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        spreadBehaviour="free"
                                        variant="filled"
                                        type="danger"
                                    />
                                :
                                    null
                            }
                            {
                                onApprove ?
                                    <Button
                                        onPress={() => requestID && onApprove(requestID)}
                                        title={localize("requests-action-approve")}
                                        icon={({
                                            color,
                                            size
                                        }) => <CheckIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        spreadBehaviour="free"
                                        variant="filled"
                                        type="success"
                                    />
                                :
                                    null
                            }
                        </View>
                    :
                        null
                }
            </View>
        </View>
    </Modal>;
};
export default CompanyDetailModal;
